/**
 * Tiny helper to detect when we're running inside a Capacitor native shell
 * (iOS / Android wrappers) vs a regular browser tab.
 *
 * Why we care: features that rely on `window.location.origin` (OAuth redirect
 * URLs, deep links, etc.) break on native because origin is `capacitor://` or
 * `http://localhost`, not the real production domain.
 */
export function isNativeApp() {
    if (typeof window === "undefined") return false;
    try {
        const cap = window.Capacitor;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            return true;
        }
        // Fallback: WebView origin pattern.
        const origin = window.location.origin || "";
        if (origin.startsWith("capacitor://") || origin === "http://localhost" || origin === "https://localhost") {
            return true;
        }
    } catch {}
    return false;
}

/**
 * App Store Guideline 3.1.1 / Google Play Payments Policy: billing surfaces
 * are platform-specific.
 *  - Web         → Stripe checkout on /pricing (full billing UI).
 *  - iOS native  → Apple In-App Purchases via the NativePaywall (RevenueCat).
 *  - Android     → Google Play Billing via the NativePaywall (RevenueCat).
 */
export function billingUIEnabled() {
    return !isNativeApp();
}

export function isIOSNative() {
    if (!isNativeApp()) return false;
    try {
        const cap = window.Capacitor;
        return !!(cap && typeof cap.getPlatform === "function" && cap.getPlatform() === "ios");
    } catch {
        return false;
    }
}

export function isAndroidNative() {
    if (!isNativeApp()) return false;
    try {
        const cap = window.Capacitor;
        return !!(cap && typeof cap.getPlatform === "function" && cap.getPlatform() === "android");
    } catch {
        return false;
    }
}

/** True on iOS or Android native — i.e. the device has a native IAP layer
 *  (StoreKit / Google Play Billing) that we drive via RevenueCat. */
export function isNativeWithIAP() {
    return isIOSNative() || isAndroidNative();
}

/** Subscription UI (plan badges, paywalls, trial wording) is allowed on web
 *  (Stripe) and native (StoreKit / Play Billing). */
export function canShowPlans() {
    return billingUIEnabled() || isNativeWithIAP();
}

// localStorage usage justification (code-review note):
// We only store the *last login email* here for prefill convenience —
// never tokens. Auth tokens go through HttpOnly cookies on web and
// Capacitor Preferences (encrypted Keychain/Keystore) on native.
// Storing email in localStorage carries no real security risk; if an
// XSS attacker has localStorage access they already control the page.
const LAST_EMAIL_KEY = "bm_last_login_email";

export function getLastLoginEmail() {
    try {
        return localStorage.getItem(LAST_EMAIL_KEY) || "";
    } catch {
        return "";
    }
}

export function rememberLoginEmail(email) {
    try {
        if (email) localStorage.setItem(LAST_EMAIL_KEY, email);
    } catch {}
}

/**
 * The canonical web URL of Bosun's Mate — used when native apps need to
 * bounce users out to the website (e.g. to subscribe, since in-app payment
 * flows aren't allowed by Google Play / App Store policy for Stripe).
 */
export const WEB_APP_URL = "https://bosunsmate.app";

/**
 * Open a URL in the system browser. On native (Capacitor) this uses the
 * @capacitor/browser plugin (Chrome Custom Tabs / SFSafariViewController).
 * On the web it just calls window.open in a new tab.
 *
 * Used for compliance: subscriptions must NOT happen inside the native
 * shell, so any "Subscribe" / "Pricing" CTA on native opens externally.
 */
export async function openExternal(url) {
    if (isNativeApp()) {
        try {
            const mod = await import("@capacitor/browser");
            await mod.Browser.open({ url, presentationStyle: "popover" });
            return;
        } catch (e) {
            console.warn("[platform] Capacitor Browser failed, falling back", e);
        }
    }
    try {
        window.open(url, "_blank", "noopener,noreferrer");
    } catch {
        window.location.href = url;
    }
}
